import { useCallback, useEffect, useState } from "react";
import type { HistoryEntry, TranslationHistoryItem } from "@models/translation";
import { translationApi } from "@services/translationApi";

// Fetch up to the backend's max (200) so client-side pagination has the full
// history to page through. Metadata rows are small, so this stays cheap.
const MAX_ENTRIES = 200;

const toEntry = (item: TranslationHistoryItem): HistoryEntry => ({
  id: item.id,
  sourceLang: item.source_lang,
  targetLangs: item.target_langs,
  mode: item.mode,
  preview: item.preview,
  // Order the UI by the most recent request time.
  createdAt: item.updated_at || item.created_at,
  contentHash: item.content_hash,
});

/**
 * Backend-backed translation history for the authenticated user.
 *
 * History is persisted server-side (keyed by user id), so it follows the user
 * across devices and sessions. The backend records an entry when a translation
 * is started; this hook reads that history and supports optimistic updates.
 *
 * Keeps the same shape ({ entries, add, remove, clear }) as the previous
 * localStorage implementation so consumers need no changes.
 */
export const useJobHistory = () => {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await translationApi.listTranslations(MAX_ENTRIES);
      setEntries(res.items.map(toEntry));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Optimistically show a newly submitted translation at the top, then
  // reconcile with the server (which records it on startTranslation). Dedupe by
  // id so re-submitting the same job doesn't duplicate the row.
  const add = useCallback(
    (entry: HistoryEntry) => {
      setEntries((prev) =>
        [entry, ...prev.filter((e) => e.id !== entry.id)].slice(0, MAX_ENTRIES),
      );
      // Reconcile in the background; the server is the source of truth.
      window.setTimeout(() => void refresh(), 500);
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      const target = entries.find((e) => e.id === id);
      // Optimistic removal.
      setEntries((prev) => prev.filter((e) => e.id !== id));
      if (target?.contentHash) {
        try {
          await translationApi.deleteHistoryEntry(target.contentHash);
        } catch {
          // Re-sync on failure so the UI reflects server state.
          void refresh();
        }
      }
    },
    [entries, refresh],
  );

  const clear = useCallback(async () => {
    const previous = entries;
    setEntries([]);
    try {
      await translationApi.clearHistory();
    } catch {
      setEntries(previous);
      void refresh();
    }
  }, [entries, refresh]);

  return { entries, add, remove, clear, loading, error, refresh };
};
